import {
  faBold,
  faItalic,
  faLink,
  faListOl,
  faListUl,
  faQuoteLeft,
  faUnderline,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import styled from 'styled-components';

// navigator.platform is deprecated, but navigator.userAgentData isn't widely
// supported yet.
const mod = /Mac|iPhone|iPad|iPod/.test(navigator.platform) ? 'Cmd' : 'Ctrl';

export type ToolbarItem =
  | 'bold'
  | 'italic'
  | 'underline'
  | 'bulletList'
  | 'numberedList'
  | 'blockquote'
  | 'link';

interface FormattingToolbarProps {
  onBold?: () => void;
  onItalic?: () => void;
  onUnderline?: () => void;
  onBulletList?: () => void;
  onNumberedList?: () => void;
  onBlockquote?: () => void;
  onLink?: () => void;
  visibleTools?: ToolbarItem[];
  alternate?: boolean;
}

export function FormattingToolbar({
  onBold,
  onItalic,
  onUnderline,
  onBulletList,
  onNumberedList,
  onBlockquote,
  onLink,
  visibleTools,
  alternate,
}: FormattingToolbarProps) {
  const isVisible = (item: ToolbarItem) =>
    !visibleTools || visibleTools.includes(item);

  return (
    // Translation Needed: all aria-labels and titles
    <FormattingIcons $alternate={alternate}>
      {isVisible('bold') && (
        <FormatIconButton
          $alternate={alternate}
          aria-label="Bold"
          title={`Bold (${mod} + B)`}
          type="button"
          onClick={onBold}
        >
          <FontAwesomeIcon icon={faBold} />
        </FormatIconButton>
      )}
      {isVisible('italic') && (
        <FormatIconButton
          $alternate={alternate}
          aria-label="Italic"
          title={`Italic (${mod} + I)`}
          type="button"
          onClick={onItalic}
        >
          <FontAwesomeIcon icon={faItalic} />
        </FormatIconButton>
      )}
      {isVisible('underline') && (
        <FormatIconButton
          $alternate={alternate}
          aria-label="Underline"
          title={`Underline (${mod} + U)`}
          type="button"
          onClick={onUnderline}
        >
          <FontAwesomeIcon icon={faUnderline} />
        </FormatIconButton>
      )}
      {isVisible('bulletList') && (
        <FormatIconButton
          $alternate={alternate}
          aria-label="Bullets"
          title="Bulleted list"
          type="button"
          onClick={onBulletList}
        >
          <FontAwesomeIcon icon={faListUl} />
        </FormatIconButton>
      )}
      {isVisible('numberedList') && (
        <FormatIconButton
          $alternate={alternate}
          aria-label="Numbered list"
          title="Numbered list"
          type="button"
          onClick={onNumberedList}
        >
          <FontAwesomeIcon icon={faListOl} />
        </FormatIconButton>
      )}
      {isVisible('blockquote') && (
        <FormatIconButton
          $alternate={alternate}
          aria-label="Blockquote"
          title="Quote"
          type="button"
          onClick={onBlockquote}
        >
          <FontAwesomeIcon icon={faQuoteLeft} />
        </FormatIconButton>
      )}
      {isVisible('link') && (
        <FormatIconButton
          $alternate={alternate}
          aria-label="Link"
          title="Insert link"
          type="button"
          onClick={onLink}
        >
          <FontAwesomeIcon icon={faLink} />
        </FormatIconButton>
      )}
    </FormattingIcons>
  );
}

const FormattingIcons = styled.div<{ $alternate?: boolean }>`
  align-items: center;
  ${({ $alternate }) =>
    !$alternate && 'animation: slideUpFadeIn 0.3s ease-out;'}
  background: ${({ $alternate }) =>
    $alternate ? 'var(--grey-100)' : 'transparent'};
  border-bottom: ${({ $alternate }) =>
    $alternate ? '1px solid var(--grey-200)' : 'none'};
  display: flex;
  gap: 1px;
  padding: ${({ $alternate }) => ($alternate ? '4px 6px' : '0')};

  @keyframes slideUpFadeIn {
    from {
      opacity: 0;
      transform: translateY(10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const FormatIconButton = styled.button<{ $alternate?: boolean }>`
  align-items: center;
  background: none;
  border: none;
  border-radius: 4px;
  color: ${({ $alternate }) =>
    $alternate ? 'var(--grey-700)' : 'var(--black)'};
  cursor: pointer;
  display: flex;
  font-size: ${({ $alternate }) => ($alternate ? '14px' : '16px')};
  justify-content: center;
  padding: ${({ $alternate }) => ($alternate ? '5px 7px' : '6px')};
  transition:
    background 0.15s,
    color 0.15s;

  ${({ $alternate }) =>
    $alternate &&
    `
      &:hover {
        background: var(--grey-200);
        color: var(--black);
      }
    `}
`;
